import { atom } from 'jotai';
import { authService } from '../services';
import type { UserRead } from '../types/api';

export interface AuthState {
  user: UserRead | null;
  isLoading: boolean;
  error: string | null;
}

export const authStateAtom = atom<AuthState>({
  user: null,
  isLoading: false,
  error: null,
});

// Derived atoms for convenience
export const isAuthenticatedAtom = atom(get => {
  const { user } = get(authStateAtom);
  return user !== null;
});

export const authLoadingAtom = atom(get => {
  const authState = get(authStateAtom);
  return authState.isLoading;
});

export const currentUserAtom = atom(get => {
  const authState = get(authStateAtom);
  return authState.user;
});

// Initialize auth from cookie
export const initializeAuthAtom = atom(null, async (_get, set) => {
  const user = _get(authStateAtom).user;

  if (user) {
    // Already initialized
    return;
  }
  set(authStateAtom, prev => ({ ...prev, isLoading: true }));

  try {
    // Try to get user info to verify cookie is valid
    const user = await authService.getCurrentUser();

    set(authStateAtom, {
      user,
      isLoading: false,
      error: null,
    });
  } catch (error) {
    // Cookie is invalid or doesn't exist - this is expected on first load
    set(authStateAtom, {
      user: null,
      isLoading: false,
      error: null, // Don't set error for initial check
    });
  }
});

// Login action
export const loginActionAtom = atom(
  null,
  async (_get, set, credentials: { email: string; password: string }) => {
    set(authStateAtom, prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await authService.login({
        username: credentials.email,
        password: credentials.password,
      });

      // Cookie is set by browser from the Set-Cookie header
      // Give browser a moment to process the cookie before fetching user
      await new Promise(resolve => setTimeout(resolve, 50));

      // Get current user info (will use the newly set cookie)
      const user = await authService.getCurrentUser();

      set(authStateAtom, {
        user,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      set(authStateAtom, prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
      throw error;
    }
  }
);

// Register action
export const registerActionAtom = atom(
  null,
  async (
    _get,
    set,
    userData: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
    }
  ) => {
    set(authStateAtom, prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await authService.register({
        email: userData.email,
        password: userData.password,
        first_name: userData.firstName ?? null,
        last_name: userData.lastName ?? null,
      });

      // After successful registration, log the user in
      await authService.login({
        username: userData.email,
        password: userData.password,
      });

      // Cookie is set by browser from the Set-Cookie header
      // Give browser a moment to process the cookie before fetching user
      await new Promise(resolve => setTimeout(resolve, 50));

      // Get current user info (will use the newly set cookie)
      const currentUser = await authService.getCurrentUser();

      set(authStateAtom, {
        user: currentUser,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      set(authStateAtom, prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Registration failed',
      }));
      throw error;
    }
  }
);

// Logout action
export const logoutActionAtom = atom(null, async (_get, set) => {
  set(authStateAtom, prev => ({ ...prev, isLoading: true }));

  try {
    // Call logout endpoint to clear cookie
    await authService.logout();

    // Reset auth state
    set(authStateAtom, {
      user: null,
      isLoading: false,
      error: null,
    });
  } catch (error) {
    // Even if logout fails, clear local state
    set(authStateAtom, {
      user: null,
      isLoading: false,
      error: null,
    });
  }
});

// Clear auth error action
export const clearAuthErrorAtom = atom(null, (_get, set) => {
  set(authStateAtom, prev => ({ ...prev, error: null }));
});
